import React, { useState } from "react";
import { MenuItem, TextField } from "@material-ui/core";
import {
	GlossaryAddWrap,
	GlossaryArrow,
	GlossaryCanonical,
	GlossaryIconBtn,
	GlossaryList,
	GlossaryNote,
	GlossaryPanelHeader,
	GlossaryPanelWrap,
	GlossaryRow,
	GlossaryTerm as GlossaryTermStyle,
} from "../styles";
import { LABELS } from "../labels";
import type { GlossaryTerm } from "../types";

interface Props {
	terms: GlossaryTerm[];
	onUpsert: (term: Omit<GlossaryTerm, "id" | "scope">) => Promise<void>;
	onRemove: (id: number) => Promise<void>;
}

const EMPTY_FORM = { term: "", canonical: "", notes: "", severity: "warn" as const };

export default function GlossaryPanel({ terms, onUpsert, onRemove }: Props) {
	const [showForm, setShowForm] = useState(false);
	const [form, setForm] = useState<Omit<GlossaryTerm, "id" | "scope">>(EMPTY_FORM);
	const [saving, setSaving] = useState(false);

	async function handleSave() {
		if (!form.term.trim() || !form.canonical.trim()) return;
		setSaving(true);
		try {
			await onUpsert(form);
			setForm(EMPTY_FORM);
			setShowForm(false);
		} finally {
			setSaving(false);
		}
	}

	function handleFormChange(patch: Partial<typeof form>) {
		setForm((prev) => ({ ...prev, ...patch }));
	}

	return (
		<GlossaryPanelWrap>
			<GlossaryPanelHeader>
				<span>{LABELS.glossaryTitle}</span>
				<GlossaryIconBtn
					onClick={() => setShowForm((v) => !v)}
					title={LABELS.glossaryAddTerm}
					style={{ color: showForm ? "#2563eb" : "#94a3b8" }}>
					<i className="fa fa-plus" />
				</GlossaryIconBtn>
			</GlossaryPanelHeader>

			<GlossaryList>
				{terms.length === 0 && (
					<GlossaryRow>
						<GlossaryNote>{LABELS.glossaryEmpty}</GlossaryNote>
					</GlossaryRow>
				)}
				{terms.map((t) => (
					<GlossaryRow key={t.id}>
						<GlossaryTermStyle>{t.term}</GlossaryTermStyle>
						<GlossaryArrow>→</GlossaryArrow>
						<GlossaryCanonical>{t.canonical}</GlossaryCanonical>
						{t.notes && <GlossaryNote>{t.notes}</GlossaryNote>}
						<span
							style={{
								fontSize: "0.6875rem",
								padding: "1px 6px",
								borderRadius: 4,
								background: t.severity === "error" ? "#fee2e2" : "#fef9c3",
								color: t.severity === "error" ? "#dc2626" : "#b45309",
								fontWeight: 600,
								whiteSpace: "nowrap",
							}}>
							{t.severity}
						</span>
						<GlossaryIconBtn
							onClick={() => onRemove(t.id)}
							title={LABELS.glossaryDelete}>
							<i className="fa fa-trash" />
						</GlossaryIconBtn>
					</GlossaryRow>
				))}
			</GlossaryList>

			{showForm && (
				<GlossaryAddWrap>
					<TextField
						size="small"
						variant="outlined"
						label={LABELS.glossaryTermLabel}
						placeholder="whalelo"
						value={form.term}
						onChange={(e) => handleFormChange({ term: e.target.value })}
						style={{ flex: 1, minWidth: 100 }}
					/>
					<TextField
						size="small"
						variant="outlined"
						label={LABELS.glossaryCanonicalLabel}
						placeholder="WhaleLO"
						value={form.canonical}
						onChange={(e) => handleFormChange({ canonical: e.target.value })}
						style={{ flex: 1, minWidth: 100 }}
					/>
					<TextField
						size="small"
						variant="outlined"
						label={LABELS.glossaryNotesLabel}
						value={form.notes}
						onChange={(e) => handleFormChange({ notes: e.target.value })}
						style={{ flex: 2, minWidth: 120 }}
					/>
					<TextField
						select
						size="small"
						variant="outlined"
						label={LABELS.glossarySeverityLabel}
						value={form.severity}
						onChange={(e) => handleFormChange({ severity: e.target.value as GlossaryTerm["severity"] })}
						style={{ width: 90 }}>
						<MenuItem value="warn">warn</MenuItem>
						<MenuItem value="error">error</MenuItem>
					</TextField>
					<button
						onClick={handleSave}
						disabled={saving || !form.term.trim() || !form.canonical.trim()}
						style={{
							padding: "6px 14px",
							border: "none",
							borderRadius: 6,
							background: saving || !form.term.trim() || !form.canonical.trim() ? "#94a3b8" : "#2563eb",
							color: "#fff",
							fontSize: "0.8125rem",
							fontWeight: 600,
							cursor: saving ? "not-allowed" : "pointer",
							whiteSpace: "nowrap",
						}}>
						{saving ? "…" : LABELS.glossarySave}
					</button>
					<button
						onClick={() => {
							setShowForm(false);
							setForm(EMPTY_FORM);
						}}
						style={{
							padding: "6px 10px",
							border: "1px solid #e2e8f0",
							borderRadius: 6,
							background: "#fff",
							color: "#374151",
							fontSize: "0.8125rem",
							cursor: "pointer",
						}}>
						{LABELS.glossaryCancel}
					</button>
				</GlossaryAddWrap>
			)}
		</GlossaryPanelWrap>
	);
}
